import {Box, Typography} from '@mui/material';

import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {getScoreLevelColors} from '../../../../../utils/getScoreLevelColors';

export type SkillScoreDimension =
  | 'coreCompetencies'
  | 'hardSkills'
  | 'tools'
  | 'domainKnowledge'
  | 'softSkills'
  | 'responsibilities';

export interface SkillScoreBreakdownItem {
  key: SkillScoreDimension;
  label: string;
  score: number | null | undefined;
  metCount?: number;
  totalCount?: number;
  available: boolean;
}

export interface Props {
  title: string;
  items: SkillScoreBreakdownItem[];
  onSelect: (key: SkillScoreDimension) => void;
  selectedKey?: SkillScoreDimension | null;
}

export const WorkforceProfileSkillScoreBreakdown = ({title, items, onSelect, selectedKey}: Props) => {
  const visibleItems = items.filter((item) => item.available && item.score != null);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <EmpionPaper sx={{border: `1px solid ${orangeColors[3]}`, borderRadius: '14px', p: 2.5, mb: 2}}>
      <Typography variant='h3' sx={{fontWeight: 600, mb: 1.5}}>
        {title}
      </Typography>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
        {visibleItems.map((item) => {
          const score = item.score as number;
          const {strokeColor} = getScoreLevelColors(score);
          const isSelected = selectedKey === item.key;
          const coverage =
            item.totalCount != null && item.totalCount > 0 && item.metCount != null
              ? `${item.metCount}/${item.totalCount}`
              : null;

          return (
            <Box
              key={item.key}
              component='button'
              type='button'
              onClick={() => onSelect(item.key)}
              sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr auto', sm: 'minmax(120px, 160px) 1fr auto auto'},
                alignItems: 'center',
                gap: {xs: 0.75, sm: 1.5},
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                border: `1px solid ${isSelected ? orangeColors[2] : 'transparent'}`,
                borderRadius: '10px',
                bgcolor: isSelected ? orangeColors[5] : 'transparent',
                px: 1,
                py: 0.75,
                font: 'inherit',
                color: 'inherit',
                transition: 'background-color 0.15s, border-color 0.15s',
                '&:hover': {bgcolor: orangeColors[5]},
              }}
            >
              <Typography variant='body2' sx={{fontWeight: 600}}>
                {item.label}
              </Typography>
              <Box
                sx={{
                  display: {xs: 'none', sm: 'block'},
                  height: 8,
                  borderRadius: 99,
                  bgcolor: orangeColors[4],
                  overflow: 'hidden',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.max(0, Math.min(100, score))}%`,
                    borderRadius: 99,
                    bgcolor: strokeColor,
                  }}
                />
              </Box>
              {coverage && (
                <Typography variant='caption' color='text.secondary' sx={{justifySelf: 'end'}}>
                  {coverage}
                </Typography>
              )}
              <Typography variant='body2' sx={{fontWeight: 700, minWidth: 40, textAlign: 'right'}}>
                {score}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </EmpionPaper>
  );
};
